import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { siteInfo, catalogueCategories } from '../data/mock';
import { Phone, Mail, MapPin, Send, CheckCircle, Download, Calendar, Users, MessageSquare } from 'lucide-react';
import CatalogueModal from '../components/CatalogueModal';

const DevisPage = () => {
  const [searchParams] = useSearchParams();
  const prefill = {
    guests: searchParams.get('invites') || '',
    area: searchParams.get('surface') || '',
    reception: searchParams.get('reception') || '',
    structure: searchParams.get('structure') || '',
    product: searchParams.get('produit') || '',
  };
  const prefilledMessage = prefill.structure
    ? "Bonjour, d'après votre simulateur, mon événement nécessite environ " +
      prefill.area +
      ' m² (' +
      (prefill.reception ? prefill.reception.toLowerCase() + ', ' : '') +
      prefill.guests +
      ' invités). Je souhaite un devis pour : ' +
      prefill.structure +
      '.'
    : prefill.product
    ? 'Bonjour, je souhaite un devis pour la location de : ' + prefill.product + '.'
    : '';

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    eventType: '',
    eventDate: '',
    location: '',
    surface: '',
    seating: prefill.reception === 'Repas assis' ? 'Assis' : prefill.reception === 'Cocktail debout' ? 'Debout (cocktail)' : '',
    guests: prefill.guests,
    categories: prefill.structure ? ['barnums'] : [],
    message: prefilledMessage,
    newsletter: false
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showCatalogueModal, setShowCatalogueModal] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCategoryToggle = (catId) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(catId)
        ? prev.categories.filter(id => id !== catId)
        : [...prev.categories, catId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quote-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
    } catch (_) {
      /* silent: still show success so the user isn't blocked */
    }
    setSubmitted(true);
    setSubmitting(false);
  };

  const eventTypes = [
    'Mariage',
    'Anniversaire',
    'Séminaire/Entreprise',
    'Festival/Fête',
    'Communion/Baptême',
    'Autre'
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-24">
      {/* Header */}
      <section className="bg-gradient-to-r from-orange-600 to-orange-700 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Demande de Devis</h1>
          <p className="text-xl text-orange-200 max-w-2xl mx-auto">
            Obtenez un devis personnalisé gratuit en quelques minutes
          </p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Form - à gauche */}
          <div className="lg:col-span-2">
            {submitted && !showCatalogueModal ? (
              <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-green-500" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Demande envoyée !</h3>
                <p className="text-gray-600">Nous vous recontacterons dans les 24h.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 shadow-sm">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Votre demande</h3>

                {/* Personal Info */}
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Prénom *</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nom *</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                </div>

                {/* Event Info */}
                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Calendar className="w-4 h-4 inline mr-1" />
                      Type d'événement
                    </label>
                    <select
                      name="eventType"
                      value={formData.eventType}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="">Sélectionnez</option>
                      {eventTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date prévue *</label>
                    <input
                      type="date"
                      name="eventDate"
                      value={formData.eventDate}
                      onChange={handleChange}
                      required
                      onInvalid={(e) => e.target.setCustomValidity('Veuillez indiquer la date prévue de votre événement')}
                      onInput={(e) => e.target.setCustomValidity('')}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Users className="w-4 h-4 inline mr-1" />
                      Nombre d'invités
                    </label>
                    <input
                      type="number"
                      name="guests"
                      value={formData.guests}
                      onChange={handleChange}
                      placeholder="Ex: 100"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                </div>

                {/* Lieu / Sol / Repas */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Lieu de l'événement *</label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    required
                    onInvalid={(e) => e.target.setCustomValidity('Veuillez indiquer le lieu de l\'événement')}
                    onInput={(e) => e.target.setCustomValidity('')}
                    placeholder="Ville, code postal ou adresse complète"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                    data-testid="devis-location"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Pose sur terrain ou surface dure ?</label>
                    <div className="flex gap-2">
                      {['Terrain', 'Surface dure', 'Les deux', 'Je ne sais pas'].map((opt) => (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => setFormData((p) => ({ ...p, surface: opt }))}
                          className={`flex-1 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                            formData.surface === opt
                              ? 'bg-orange-500 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          data-testid={`devis-surface-${opt}`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Repas assis ou debout (cocktail) ?</label>
                    <div className="flex gap-2">
                      {['Assis', 'Debout (cocktail)', 'Les deux'].map((opt) => (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => setFormData((p) => ({ ...p, seating: opt }))}
                          className={`flex-1 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                            formData.seating === opt
                              ? 'bg-orange-500 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          data-testid={`devis-seating-${opt}`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Categories */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">Matériel souhaité</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {catalogueCategories.map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => handleCategoryToggle(cat.id)}
                        className={`p-3 rounded-xl text-sm font-medium text-left transition-colors ${
                          formData.categories.includes(cat.id)
                            ? 'bg-orange-500 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Message */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MessageSquare className="w-4 h-4 inline mr-1" />
                    Votre message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    required
                    onInvalid={(e) => e.target.setCustomValidity('Veuillez décrire votre projet')}
                    onInput={(e) => e.target.setCustomValidity('')}
                    placeholder="Décrivez votre projet..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                  />
                </div>

                {/* Newsletter */}
                <div className="mb-8">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="newsletter"
                      checked={formData.newsletter}
                      onChange={handleChange}
                      className="w-5 h-5 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                    />
                    <span className="text-sm text-gray-600">
                      Je souhaite recevoir le catalogue et les offres par email
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  className="w-full flex items-center justify-center gap-2 py-4 bg-orange-500 text-white font-semibold rounded-xl hover:bg-orange-600 transition-colors"
                >
                  <Send className="w-5 h-5" />
                  Envoyer ma demande
                </button>
              </form>
            )}
          </div>

          {/* Contact Info - à droite */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-8 shadow-sm sticky top-28">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Nous Contacter</h3>
              
              <div className="space-y-6 mb-8">
                <div className="p-4 bg-orange-50 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-orange-500" />
                    <span className="font-semibold text-gray-900">Gainneville</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">Interlocuteur : <strong>Mehdi</strong></p>
                  <a href="tel:0698430992" className="flex items-center gap-2 text-orange-600 font-medium">
                    <Phone className="w-4 h-4" />
                    06 98 43 09 92
                  </a>
                </div>

                <div className="p-4 bg-orange-50 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-orange-500" />
                    <span className="font-semibold text-gray-900">Cauville-sur-Mer</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">Interlocuteur : <strong>Damien</strong></p>
                  <a href="tel:0623893298" className="flex items-center gap-2 text-orange-600 font-medium">
                    <Phone className="w-4 h-4" />
                    06 23 89 32 98
                  </a>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Email</div>
                    <a href="mailto:contact@barnum-evenement.fr" className="font-medium text-gray-900 hover:text-orange-600">
                      contact@barnum-evenement.fr
                    </a>
                  </div>
                </div>
              </div>

              <button
                onClick={() => setShowCatalogueModal(true)}
                className="flex items-center justify-center gap-2 w-full py-3 bg-orange-100 text-orange-600 font-medium rounded-xl hover:bg-orange-200 transition-colors"
              >
                <Download className="w-5 h-5" />
                Télécharger le Catalogue 2026
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Catalogue Modal */}
      <CatalogueModal isOpen={showCatalogueModal} onClose={() => setShowCatalogueModal(false)} />
    </div>
  );
};

export default DevisPage;
